import { isDefined, isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';

export type UpdateCallOrderRequest = {
  tileId: string;

  orderRef: string;
  currency1: string;
  currency2: string;
  limitPrice?: number;
  startDateUtc: string | null;
  endDateUtc: string | null;
};

export type UpdateCallOrderBody = {
  orderRef: string;
  ccyPair: string; // 'EUR/USD';
  limitPrice: string; // '1';
  startDateUtc: string | null;
  endDateUtc: string | null;
};

export const updateCallOrder = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, UpdateCallOrderRequest>({
    query: (request) => ({
      url: '/oms/update/Call',
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformResponse: (response: string | { validationResponseDetailContent: ValidationResponseDetailContent[] }) => {
      return {
        type: 'success',
        orderId: response as string, // assuming the response is a string with the order ID
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | { validationResponseDetailContent: ValidationResponseDetailContent[] };
      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
                    response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );

        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }

        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (request: UpdateCallOrderRequest): UpdateCallOrderBody => {
  return {
    orderRef: request.orderRef,
    ccyPair: `${request.currency1}/${request.currency2}`,
    startDateUtc: isDefined(request.startDateUtc) ? request.startDateUtc : '',
    endDateUtc: isDefined(request.endDateUtc) ? request.endDateUtc : '',
    limitPrice: isDefined(request.limitPrice) ? String(request.limitPrice) : '',
  };
};

function isValidateOrderResponse(
  response: unknown,
): response is { validationResponseDetailContent: ValidationResponseDetailContent[]; isReadyToSubmit: boolean } {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToSubmit' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
